import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';

import Text from '~components/atoms/Text';
import ActionButton from '~components/atoms/ActionButton';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { CustomerIdTypeEnum, FundTypeEnum, GoldTypeEnum, MarketStatusEnum, MetalTypeEnum, ModuleEnum, PermissionsEnum, SilverTypeEnum, TransactionActionEnum, TransactionStatusEnum } from '~utils/enums';

import TabPageContainer from '~components/atoms/TabPageContainer';
import TabPageHeaderWrapper from '~components/atoms/TabPageHeaderWrapper';
import DownloadCSV from '~components/molecules/DownloadCSV';
import GoldTransactionSearchAndFilterBar from './GoldTransactionSearchAndFilterBar';
import GoldTransactionTable from './GoldTransactionTable';
import GoldTransactionPaginator from './GoldTransactionPaginator';
import BuyOrSellGoldModal from './BuyOrSellGoldModal/BuyOrSellGoldModal';
import GoldTransactionDetailsModal from './GoldTransactionDetailsModal';
import ConfirmUpdateTransactionStatusModal from './ConfirmUpdateTransactionStatusModal';

import { initialState as settingInitialState } from '~redux/slices/setting';

import UtilFunctions from '~utils/functions';

import * as Styles from './_buyAndSellGold.module.scss';

const GoldManagement = () => {
    const {
        Misc: {
            dateTimeFormat,
        },
    } = UtilFunctions;

    const dispatch = useAppDispatch();

    const setIsConfirmationModalOpen = (isOpen: boolean) => {
        dispatch(combinedActions.goldSetIsConfirmationModalOpen(isOpen));
    };
    const setIsTransactionDetailsModalOpen = (isOpen: boolean) => {
        dispatch(combinedActions.goldSetIsTransactionDetailsModalOpen(isOpen));
    };

    const exportGoldTransactionListing = () => {isReadyToExportRef.current = true; dispatch(combinedActions.goldExportGoldTransactionListingAttempt());};
    const clearGoldTransactionListingForExport = () => dispatch(combinedActions.goldClearGoldTransactionListingForExport());

    const isBuyOrSellGoldModalOpen = useAppSelector(state => state.gold.modals.isBuyOrSellGoldModalOpen);
    const isGoldTransactionDetailsModalOpen = useAppSelector(state => state.gold.modals.isTransactionDetailsModalOpen);
    const isMarketOpen = useAppSelector(state => state.dashboard.dashboardMarketStatus.status);

    const transactionListingIndex = useAppSelector(state => state.gold.buyAndSellGold.transactionListing.index);
    const selectedTransactionType = useAppSelector(state => state.gold.buyAndSellGold.transactionType);

    const getGoldTransactionListingForExportIsLoading = useAppSelector(state => state.gold.loadings.exportGoldTransactionListing);
    const goldTransactionListingForExport = useAppSelector(state => state.gold.goldTransactionListingForExport);

    // const initialFilters = settingInitialState.discounts.filters;

    const createAccessModules = useAppSelector(state => state.auth.userDetails.access).find(access => access.permission === PermissionsEnum.Create)?.modules || [];

    const [isExportingGoldTransactionListing, setIsExportingGoldTransactionListing] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const isReadyToExportRef = useRef(false);
    const prevLoadingRef = useRef(false);

    useEffect(() => {
        dispatch(combinedActions.branchGetAllBranchesAttempt());
        dispatch(combinedActions.authGetIsMarketOpenAttempt());
        dispatch(combinedActions.dashboardGetDashboardMarketStatusAttempt());

        const intervalId = setInterval(() => {
            dispatch(combinedActions.dashboardGetDashboardMarketStatusAttempt());
            dispatch(combinedActions.dashboardGetDashboardMarketStatusAttempt());
        }, 30000);

        return () => {
            clearInterval(intervalId); // cleanup on unmount
        };
    }, [dispatch]);

    useEffect(() => {
        dispatch(combinedActions.goldGetTransactionListingAttempt());
        // dispatch(combinedActions.settingSetDiscountsListingFilters(initialFilters));
        dispatch(combinedActions.settingGetAllDiscountAttempt());
        dispatch(combinedActions.branchGetAllBranchesAttempt());
    }, [transactionListingIndex]);

    const listHeader = [
        'TRANSACTION ID',
        'SERIAL NUMBER',
        'EARMARK TRANSACTION',
        'PROJECT NAME',
        'CUSTOMER NAME',
        'CUSTOMER ID TYPE',
        'CUSTOMER ID NUMBER',
        'CIF NUMBER',
        'BRANCH CODE',
        'BRANCH NAME',
        'REGION NAME',
        'STATUS',
        'PRODUCT NAME',
        'DESCRIPTION',
        'TOTAL WEIGHT PER UNIT (gram)',
        'API BASED PRICE (RM)',
        'DISCOUNT NAME',
        'DISCOUNT CODE',
        'DISCOUNT TYPE',
        'DISCOUNT RATE',
        'FLAT DISCOUNT RATE (RM)',
        'AFFIN SPLIT PER UNIT (RM)',
        'AFFIN SPLIT RATE PER UNIT (%)',
        'AFFIN SPLIT PER UNIT AFTER DISCOUNT (RM)',
        'AFFIN SPLIT RATE PER UNIT AFTER DISCOUNT (%)',
        'MINTING COST PER UNIT (RM)',
        'MINTING RATE PER UNIT (%)',
        'TTT SPLIT PER UNIT (RM)',
        'TTT SPLIT RATE PER UNIT (%)',
        'TOTAL VALUE PER UNIT (RM)',
        'NET VALUE PER PRODUCT (RM)',
        'QUANTITY',
        'AFFIN SPLIT TOTAL (RM)',
        'MINTING COST TOTAL (RM)',
        'TTT SPLIT TOTAL (RM)',
        'TOTAL NET VALUE (RM)',
        'TOTAL WEIGHT (gram)',
        'ACTION',
        'FUND TYPE',
        'GOLD TYPE',
        'SILVER TYPE',
        'BRANCH TELLER NAME',
        'SALES PERSONNEL NAME',
        'REFERRAL NAME',
        'TRANSACTION DATE',
        'TRANSACTION UPDATED AT',
    ];

    const listData = goldTransactionListingForExport.map(transaction => {
        const {
            transactionId,
            regionName,
            branchId,
            branchName,
            branchCode,
            action,
            status,
            branchTellerName,
            branchTellerId,
            customerId,
            name,
            CIFNo,
            idType,
            idNo,
            child,
            fundType,
            goldType,
            silverType,
            productId,
            productName,
            projectName,
            description,
            physicalGoldWeight,
            physicalSilverWeight,
            image,
            serialNo,
            discountName,
            discountCode,
            discountTransactionType,
            discountRate,
            discountRatePercentage,
            affinSplitAfterDiscount,
            totalAffinSplitAfterDiscount,
            netValuePerTransactionItem,
            amount,
            goldPrice,
            totalNetValue,
            crc,
            dsig,
            createdAt,
            updatedAt,
            salesPersonnelName,
            referralName,
            isEarmarkTransaction,
            tttSplitSetting,
            affinSplitSetting,
            affinSplitSettingAfterDiscount,
            transactionMargin: {
                amount: tmAmount,
                totalMintingCost,
                mintingCostSetting,
                totalMargin,
                tttSplit,
                affinSplit,
                grandTotal,
                baseAPIPrice,
                totalTTTSplit,
                totalAffinSplit,
                mintingCost,
                weightedGoldPrice,
            },
            totalWeight,
            metalType,
        } = transaction;

        const massagedCode = (serialNo || [])
            .filter(item => item)
            .join('|');

        const getAffinSplitRatePerUnit = (affinSplitRate: number) => {

            if (fundType === FundTypeEnum['Conventional'] && goldType === GoldTypeEnum['PhysicalGold']) {
                return (Math.round(Number(affinSplitRate) * 10000) / 10000).toFixed(4);
            }

            return (Math.round(Number(affinSplitRate) * 100) / 100).toFixed(2);
        };

        const getDiscountTransactionType = (discountType: TransactionActionEnum) => {
            if (discountType === TransactionActionEnum['Bank Buy']) return 'MARKUP';
            if (discountType === TransactionActionEnum['Bank Sell']) return 'DISCOUNT';

            return '-';
        };

        return {
            transactionId: transactionId || '-',
            code: massagedCode.length > 0 ? massagedCode : '-',
            earmarkTransaction: isEarmarkTransaction ? 'Yes' : '-',
            projectName: projectName || '-',
            customerName: name || '-',
            customerIdType: idType ? CustomerIdTypeEnum[idType] : '-',
            customerId: idNo || '-',
            cifNo: CIFNo || '-',
            branchCode: branchCode || '-',
            branchName: branchName || '-',
            regionName: regionName || '-',
            status: status ? TransactionStatusEnum[status] : '-',
            productName: productName || '-',
            description: description || '-',
            totalWeightPerUnit:
                metalType === MetalTypeEnum.Gold
                    ? (
                        goldType === GoldTypeEnum.PhysicalGold
                            ? (physicalGoldWeight
                                ? (Math.round(physicalGoldWeight * 1000) / 1000).toFixed(3)
                                : '1.000')
                            : '-'
                    )
                    : (
                        silverType === SilverTypeEnum.PhysicalSilver
                            ? (physicalSilverWeight
                                ? (Math.round(physicalSilverWeight * 1000) / 1000).toFixed(3)
                                : '1.000')
                            : '-'
                    ),
            apiBasedGoldPrice: baseAPIPrice ? (Math.round(Number(baseAPIPrice) * 100) / 100).toFixed(2) : '-',
            discountName: discountName || '-',
            discountCode: discountCode || '-',
            discountType: discountTransactionType ? getDiscountTransactionType(discountTransactionType) : '-',
            discountRate: discountRatePercentage || '-',
            flatDiscountRate: discountRate || '-',
            affinSplitPerUnit: affinSplit ? (Math.round(Number(affinSplit) * 100) / 100).toFixed(2) : '-',
            affinSplitRatePerUnit: affinSplitSetting ? getAffinSplitRatePerUnit(affinSplitSetting) : '-',
            affinSplitPerUnitAfterDiscount: affinSplitAfterDiscount ? (Math.round(affinSplitAfterDiscount * 100) / 100).toFixed(2) : '-',
            affinSplitRatePerUnitAfterDiscount: affinSplitSettingAfterDiscount ? (Math.round(affinSplitSettingAfterDiscount * 100) / 100).toFixed(2) : '-',
            mintingCostPerUnit: mintingCost ? (Math.round(Number(mintingCost) * 100) / 100).toFixed(2) : '-',
            mintingRatePerUnit: mintingCostSetting ? (Math.round(Number(mintingCostSetting) * 100) / 100).toFixed(2) : '-',
            tttSplitPerUnit: tttSplit ? (Math.round(Number(tttSplit) * 100) / 100).toFixed(2) : '-',
            tttSplitRatePerUnit: tttSplitSetting ? (Math.round(Number(tttSplitSetting) * 100) / 100).toFixed(2) : '-',
            totalValuePerUnit: weightedGoldPrice ? (Math.round(weightedGoldPrice * 100) / 100).toFixed(2) : '0',
            netValuePerTransactionItem: netValuePerTransactionItem || '-',
            quantity: amount ? (Math.round(amount * 1000) / 1000).toFixed(3) : '-',
            affinSplitTotal: totalAffinSplit ? (Math.round(Number(totalAffinSplit) * 100) / 100).toFixed(2) : '-',
            mintingCostTotal: totalMintingCost ? (Math.round(Number(totalMintingCost) * 100) / 100).toFixed(2) : '-',
            tttSplitTotal: totalTTTSplit ? (Math.round(Number(totalTTTSplit) * 100) / 100).toFixed(2) : '-',
            totalNetValue: tmAmount ? (Math.round(Number(tmAmount) * 100) / 100).toFixed(2) : '-',
            totalWeight: totalWeight ? (Math.round(totalWeight * 1000) / 1000).toFixed(3) : '-',
            action: action ? TransactionActionEnum[action] : '-',
            fundType: fundType ? FundTypeEnum[fundType] : '-',
            goldType: goldType ? GoldTypeEnum[goldType].split(/(?=[A-Z])/).join(' ') : '-',
            silverType: silverType ? SilverTypeEnum[silverType].split(/(?=[A-Z])/).join(' ') : '-',
            branchTellerName: branchTellerName || '-',
            salesPersonnelName: salesPersonnelName || '-',
            referralName: referralName || '-',
            createdAt: createdAt ? dateTimeFormat(createdAt) : '-',
            updatedAt: updatedAt ? dateTimeFormat(updatedAt) : '-',
        };
    });

    const handleExportGoldTransactionListingCleanup = () => {
        clearGoldTransactionListingForExport();
        setIsExportingGoldTransactionListing(false);
        isReadyToExportRef.current = false;
    };

    const handleActionButton = (type: TransactionActionEnum) => {
        dispatch(combinedActions.goldSetIsBuyOrSellGoldModalOpen(!isBuyOrSellGoldModalOpen));
        dispatch(combinedActions.goldSetTransactionType(type));
    };

    useEffect(() => {
        if (prevLoadingRef.current && !getGoldTransactionListingForExportIsLoading && isReadyToExportRef.current) {
            if (listData.length === 0) {
                const csv = listHeader.join(',') + '\r\n';
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${dayjs().format('DD-MM-YYYY')} Gold Transaction Listing.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                handleExportGoldTransactionListingCleanup();
            }
        }

        prevLoadingRef.current = getGoldTransactionListingForExportIsLoading;
    }, [getGoldTransactionListingForExportIsLoading]);

    return (
        <TabPageContainer>
            <TabPageHeaderWrapper>
                <Text
                    label='Transactions List'
                    className={Styles.title}
                    isBold
                />

                <div className={Styles['action-container']}>
                    <DownloadCSV
                        exportTestId='transaction-list-export-button'
                        buttonLabel='Export Gold Transaction Listing to CSV'
                        header={listHeader}
                        data={listData}
                        fileName={dayjs().format('DD-MM-YYYY').concat(' Gold Transaction Listing')}
                        onClick={exportGoldTransactionListing}
                        cleanup={handleExportGoldTransactionListingCleanup}
                        isLoading={getGoldTransactionListingForExportIsLoading || isExportingGoldTransactionListing}
                    />

                    {createAccessModules.find(module => module === ModuleEnum.BuySell)
                        && (
                            isMarketOpen === MarketStatusEnum.Open ?
                                <>
                                    <ActionButton
                                        label='Bank Sell'
                                        onClick={() => handleActionButton(TransactionActionEnum['Bank Sell'])}
                                        testId='transaction-bank-sell-open-button'
                                    />

                                    <ActionButton
                                        label='Bank Buy'
                                        onClick={() => handleActionButton(TransactionActionEnum['Bank Buy'])}
                                        testId='transaction-bank-buy-open-button'
                                    />
                                </>
                                :
                                <div
                                    className={Styles['market-closed']}
                                    onMouseEnter={() => setShowTooltip(true)}
                                    onMouseLeave={() => setShowTooltip(false)}
                                >
                                    <ActionButton
                                        label='Bank Sell'
                                        onClick={() => handleActionButton(TransactionActionEnum['Bank Sell'])}
                                        className={Styles['disable-button']}
                                        disabled
                                        testId='transaction-bank-sell-close-button'
                                    />

                                    <ActionButton
                                        label='Bank Buy'
                                        onClick={() => handleActionButton(TransactionActionEnum['Bank Buy'])}
                                        className={Styles['disable-button']}
                                        disabled
                                        testId='transaction-bank-buy-close-button'
                                    />

                                    {
                                        showTooltip && (
                                            <>
                                                <div className={Styles['tooltip-container']}>
                                                    Market Closed from 4:30pm to 9:30am
                                                </div>

                                                <div className={Styles['tooltip-container2']}>
                                                    Market Closed from 4:30pm to 9:30am
                                                </div>
                                            </>
                                        )
                                    }
                                </div>
                        )
                    }
                </div>
            </TabPageHeaderWrapper>

            <GoldTransactionSearchAndFilterBar />

            <GoldTransactionTable />

            <GoldTransactionPaginator />

            <BuyOrSellGoldModal
                isOpen={isBuyOrSellGoldModalOpen}
                onClose={() => setIsConfirmationModalOpen(true)}
                transactionType={selectedTransactionType}
            />

            <GoldTransactionDetailsModal
                isOpen={isGoldTransactionDetailsModalOpen}
                onClose={() => setIsTransactionDetailsModalOpen(false)}
            />

            <ConfirmUpdateTransactionStatusModal />
        </TabPageContainer>
    );
};

export default GoldManagement;
